import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { IconArrowLeft, IconPencil } from "@tabler/icons-react";
import {
  AdminPageHeader,
  Badge,
  Button,
  Card,
  Group,
  IconAction,
  TablaSkeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import type { TipoDeSolicitudCatalogo } from "@/features/solicitudes-catalogo/services/solicitudes-catalogo.api";
import { useTiposSolicitudCatalogoAdmin } from "../hooks/use-tipos-solicitud-catalogo-admin";
import { EditarTipoSolicitudDialog } from "./EditarTipoSolicitudDialog";

/**
 * Configuración del módulo Solicitudes de catálogo (spec §5.2) · un renglón por tipo
 * (sembrados · no se crean ni eliminan): activo, roles responsables, atención automática,
 * motivo de rechazo obligatorio y los textos que ve el solicitante.
 */
export function AdminTiposSolicitudCatalogoPage() {
  const navigate = useNavigate();
  const { data } = useTiposSolicitudCatalogoAdmin();
  const [editando, setEditando] = useState<TipoDeSolicitudCatalogo | null>(null);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <AdminPageHeader
        title="Tipos de solicitud de catálogo"
        description="Qué catálogos pueden solicitar los usuarios, qué roles atienden cada tipo y cómo se resuelven."
        actions={
          <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => void navigate({ to: "/admin" })}>
            Volver
          </Button>
        }
      />
      <Card padding="lg">
        <div className="overflow-x-auto">
          <Table verticalSpacing="sm" minWidth={860}>
            <Thead>
              <Tr>
                <Th>Tipo</Th>
                <Th>Estado</Th>
                <Th>Roles responsables</Th>
                <Th>Atención automática</Th>
                <Th>Rechazo exige motivo</Th>
                <Th>Texto del botón</Th>
                <Th align="center">Acciones</Th>
              </Tr>
            </Thead>
            {!data ? (
              <TablaSkeleton dentroDeTabla columnas={[{}, {}, {}, {}, {}, {}, { align: "center" }]} filas={6} />
            ) : (
              <Tbody>
                {data.map((t) => (
                  <Tr key={t.tipo}>
                    <Td>
                      <Text size="sm" fw={600}>
                        {t.nombre}
                      </Text>
                    </Td>
                    <Td>
                      <Badge variant="light" size="sm" color={t.activo ? "exito" : "slate"}>
                        {t.activo ? "Activo" : "Inactivo"}
                      </Badge>
                    </Td>
                    <Td>
                      <Group gap={4}>
                        {t.rolesResponsables.length === 0 ? (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        ) : (
                          t.rolesResponsables.map((r) => (
                            <Badge key={r} variant="outline" size="sm">
                              {r}
                            </Badge>
                          ))
                        )}
                      </Group>
                    </Td>
                    <Td>
                      <Text size="sm">{t.atencionAutomatica ? "Sí" : "No"}</Text>
                    </Td>
                    <Td>
                      <Text size="sm">{t.rechazoExigeMotivo ? "Sí" : "No"}</Text>
                    </Td>
                    <Td className="max-w-60">
                      <Text size="sm" className="line-clamp-1" title={t.textoBoton}>
                        {t.textoBoton}
                      </Text>
                    </Td>
                    <Td align="center">
                      <IconAction label="Configurar" size="sm" onClick={() => setEditando(t)}>
                        <IconPencil size={16} stroke={1.7} />
                      </IconAction>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            )}
          </Table>
        </div>
      </Card>
      <EditarTipoSolicitudDialog tipo={editando} onCerrar={() => setEditando(null)} />
    </div>
  );
}
